/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
import type { BriefTemplateLevel } from '@inference.engine/assets-api-client/web';

/**
 * `entities/brief-template` TanStack Query key factory.
 *
 * Tuple shapes are pinned by `query-keys.test.ts` (cache contract).
 *
 * Surface:
 *   - `BRIEF_TEMPLATES_QUERY_KEYS.all`                    → `['brief-templates']`
 *   - `BRIEF_TEMPLATES_QUERY_KEYS.list()`                 → `['brief-templates', 'list', {}]`
 *   - `BRIEF_TEMPLATES_QUERY_KEYS.list({ status: ... })`  → `['brief-templates', 'list', { status }]`
 *   - `BRIEF_TEMPLATES_QUERY_KEYS.detail(id)`             → `['brief-templates', 'detail', id]`
 *   - `BRIEF_TEMPLATES_QUERY_KEYS.resolution(id)`         → `['brief-templates', 'detail', id, 'resolution']`
 */

export interface BriefTemplateListFilters {
  status?: 'draft' | 'published';
  level?: BriefTemplateLevel;
  enabled?: boolean;
}

export const BRIEF_TEMPLATES_QUERY_KEYS = {
  /** Root namespace for every brief-templates cache entry. */
  all: ['brief-templates'] as const,

  /** List queries, scoped by optional filters so different filter combos get separate cache entries. */
  list: (filters: BriefTemplateListFilters = {}) =>
    [...BRIEF_TEMPLATES_QUERY_KEYS.all, 'list', filters] as const,

  /** Single brief template by id. */
  detail: (id: string) => [...BRIEF_TEMPLATES_QUERY_KEYS.all, 'detail', id] as const,

  /**
   * Deep-link resolution of a brief template id to its owning workspace/org
   * (`GET /brief-templates/:id/resolve-workspace`). Extends the detail tuple so a
   * detail invalidation also re-resolves.
   */
  resolution: (id: string) => [...BRIEF_TEMPLATES_QUERY_KEYS.detail(id), 'resolution'] as const,
} as const;
